import { Injectable, computed, inject, signal } from '@angular/core';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';

import { environment } from '../../../environments/environment';

/**
 * OASIS-only auth service. Wraps `OAuthService` with the Cognito Hosted UI
 * config and exposes a small reactive surface (signals) for the rest of the
 * SPA to consume.
 *
 * Token strategy:
 *  - PKCE `code` flow against Cognito Hosted UI.
 *  - We send the **access token** as `Authorization: Bearer …` because the
 *    OASIS-external API Gateway authorizer expects `tokenUse: 'access'`.
 *  - We rely on Cognito returning a refresh token for the authorization-code
 *    grant. Cognito does not implement the `offline_access` scope, so it is
 *    deliberately absent from `scopes`. We then use the standard OAuth
 *    refresh-token grant — not iframe silent refresh — by configuring
 *    `useSilentRefresh: false` and enabling `setupAutomaticSilentRefresh()`
 *    (which routes to `refreshToken()` when `useSilentRefresh` is false).
 */
@Injectable({ providedIn: 'root' })
export class OasisAuthService {
	private readonly oauth = inject(OAuthService);

	/** Reactive flag mirroring `oauth.hasValidAccessToken()`. */
	private readonly _isAuthenticated = signal(false);
	readonly isAuthenticated = this._isAuthenticated.asReadonly();

	/** Identity claims (sub, email, …). Empty object when anonymous. */
	private readonly _claims = signal<Record<string, unknown>>({});
	readonly claims = this._claims.asReadonly();

	/** Convenience for the email claim. */
	readonly email = computed(() => (this._claims()['email'] as string | undefined) ?? null);

	/**
	 * Configure the library, attempt to complete an in-progress code flow
	 * (when returning from the Hosted UI), then schedule automatic refresh.
	 *
	 * Called from the `APP_INITIALIZER` in `app.config.ts` so the rest of
	 * the app can rely on `isAuthenticated()` from first render.
	 */
	async init(): Promise<void> {
		const config: AuthConfig = {
			issuer: environment.cognito.issuer,
			clientId: environment.cognito.clientId,
			redirectUri: environment.cognito.redirectUri,
			postLogoutRedirectUri: environment.cognito.postLogoutRedirectUri,
			responseType: 'code',
			scope: environment.cognito.scopes,
			showDebugInformation: !environment.production,

			// Use the standard OAuth refresh-token grant (Cognito's
			// `ALLOW_REFRESH_TOKEN_AUTH`) instead of iframe silent refresh.
			useSilentRefresh: false,

			// Cognito returns the audience as the client_id on access tokens
			// in some configurations; this avoids a strict-mode mismatch.
			strictDiscoveryDocumentValidation: false,

			// Always send the OIDC `nonce` and verify on return.
			disableAtHashCheck: true,
		};

		this.oauth.configure(config);

		// Subscribe before discovery so we capture token_received during
		// the redirect-back leg of the code flow.
		this.oauth.events.subscribe(() => this.refreshState());

		try {
			await this.oauth.loadDiscoveryDocumentAndTryLogin();
		} catch (err) {
			console.error('[OasisAuth] discovery / tryLogin failed', err);
		}

		// Schedule a refresh ~75% through the access token's lifetime.
		// With `useSilentRefresh: false`, this calls `refreshToken()` under
		// the hood (refresh-token grant against Cognito's /oauth2/token).
		if (this.oauth.hasValidAccessToken()) {
			this.oauth.setupAutomaticSilentRefresh({}, 'access_token');
		}

		this.refreshState();
	}

	/** Begin the Hosted UI sign-in flow (full top-level redirect). */
	login(targetUrl?: string): void {
		this.oauth.initLoginFlow(targetUrl);
	}

	/**
	 * Begin the Hosted UI flow with `screen_hint=signup` so Cognito lands
	 * the user on the Create Account tab.
	 */
	signUp(targetUrl?: string): void {
		this.oauth.initLoginFlow(targetUrl, { screen_hint: 'signup' });
	}

	/**
	 * Read (and clear) the post-login destination captured by `authGuard`
	 * before the Hosted UI redirect. Returns null when there's nothing to
	 * resume, in which case callers should fall back to the dashboard.
	 *
	 * `initLoginFlow(target)` packs the target into the OAuth `state` param as
	 * `nonce + ';' + encodeURIComponent(target)`, and the library hands back
	 * only the user portion on `oauth.state` — still percent-encoded, because
	 * `parseState()` splits on the separator without decoding. So we decode
	 * here: navigating to the raw value would treat '%2Fapplication%2Fessays'
	 * as one bogus segment and fall through to the '**' route.
	 *
	 * Consume-once by design. We clear `oauth.state` so a later visit to the
	 * landing page in the same session can't replay a stale destination.
	 */
	consumePostLoginTarget(): string | null {
		const raw = this.oauth.state;
		if (!raw) return null;
		this.oauth.state = '';

		let target: string;
		try {
			target = decodeURIComponent(raw);
		} catch {
			// Malformed percent-encoding — ignore rather than throw mid-navigation.
			return null;
		}

		// Only app-internal absolute paths. Rejecting '//host' and anything with
		// a scheme keeps a tampered state param from becoming an open redirect.
		if (!target.startsWith('/') || target.startsWith('//')) return null;
		return target;
	}

	/** End the local session and redirect to Cognito's logout endpoint. */
	logout(): void {
		this.oauth.logOut({
			client_id: this.oauth.clientId,
			logout_uri: typeof window !== 'undefined' ? `${window.location.origin}` : '',
		});
	}

	/** Current access token (empty string when unauthenticated). */
	accessToken(): string {
		return this.oauth.getAccessToken() ?? '';
	}

	private refreshState(): void {
		const valid = this.oauth.hasValidAccessToken();
		this._isAuthenticated.set(valid);
		this._claims.set(valid ? (this.oauth.getIdentityClaims() ?? {}) : {});
	}
}
